// src/features/blog/components/BlogForm.jsx
import React, { useState, useRef } from "react";
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  Paper,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Stack,
  Alert,
  CircularProgress,
  Avatar,
  IconButton,
  ImageList,
  ImageListItem,
} from "@mui/material";
import {
  Save,
  Cancel,
  Image as ImageIcon,
  Delete,
  Upload,
  Add,
} from "@mui/icons-material";
import { BLOG_CATEGORIES, BLOG_STATUS } from "../types/blogTypes";
import { generateSlug, validateBlogPost } from "../utils/blogHelpers";

const BlogForm = ({
  initialData,
  onSubmit,
  onCancel,
  loading = false,
  onImageUpload,
}) => {
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState(() => {
    if (initialData) {
      return initialData;
    }
    return {
      title: "",
      slug: "",
      excerpt: "",
      content: "",
      featuredImage: "",
      images: [],
      category: "",
      tags: [],
      status: "draft",
      seo: {
        title: "",
        description: "",
        keywords: [],
        ogImage: "",
        ogTitle: "",
        ogDescription: "",
      },
      featured: false,
      allowComments: true,
      isPublic: true,
    };
  });

  const [tagInput, setTagInput] = useState("");
  const [errors, setErrors] = useState({});
  const [uploading, setUploading] = useState(false);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (field === "title") {
      // Auto-generate slug
      setFormData((prev) => ({ ...prev, slug: generateSlug(value) }));
    }
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const handleSeoChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      seo: { ...prev.seo, [field]: value },
    }));
  };

  const handleTagAdd = () => {
    if (tagInput && !formData.tags.includes(tagInput)) {
      setFormData((prev) => ({
        ...prev,
        tags: [...prev.tags, tagInput],
      }));
      setTagInput("");
    }
  };

  const handleTagRemove = (tagToRemove) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.filter((tag) => tag !== tagToRemove),
    }));
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      setUploading(true);
      try {
        const result = await onImageUpload(file);
        setFormData((prev) => ({
          ...prev,
          images: [...prev.images, result.url],
        }));
        // Set as featured if no featured image
        if (!formData.featuredImage) {
          setFormData((prev) => ({ ...prev, featuredImage: result.url }));
        }
      } catch (error) {
        console.error("Upload failed:", error);
      } finally {
        setUploading(false);
      }
    }
  };

  const handleRemoveImage = (imageUrl) => {
    setFormData((prev) => ({
      ...prev,
      images: prev.images.filter((img) => img !== imageUrl),
    }));
    if (formData.featuredImage === imageUrl) {
      setFormData((prev) => ({
        ...prev,
        featuredImage:
          prev.images.length > 1
            ? prev.images.find((img) => img !== imageUrl)
            : "",
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const validation = validateBlogPost(formData);
    if (!validation.isValid) {
      setErrors(validation.errors);
      return;
    }

    onSubmit && onSubmit(formData);
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ p: 2 }}>
      <Grid container spacing={3}>
        {/* Header */}
        <Grid item xs={12}>
          <Typography variant="h6" fontWeight="bold">
            {initialData ? "Edit Blog Post" : "Create New Blog Post"}
          </Typography>
          <Divider sx={{ mt: 2 }} />
        </Grid>

        {/* Basic Info */}
        <Grid item xs={12} md={8}>
          <TextField
            fullWidth
            label="Title"
            value={formData.title || ""}
            onChange={(e) => handleChange("title", e.target.value)}
            required
            size="small"
            error={!!errors.title}
            helperText={errors.title}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <FormControl fullWidth size="small">
            <InputLabel>Status</InputLabel>
            <Select
              value={formData.status || "draft"}
              onChange={(e) => handleChange("status", e.target.value)}
              label="Status"
            >
              <MenuItem value={BLOG_STATUS.DRAFT}>Draft</MenuItem>
              <MenuItem value={BLOG_STATUS.PENDING_REVIEW}>
                Pending Review
              </MenuItem>
              <MenuItem value={BLOG_STATUS.SCHEDULED}>Scheduled</MenuItem>
              <MenuItem value={BLOG_STATUS.PUBLISHED}>Published</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Slug"
            value={formData.slug || ""}
            onChange={(e) => handleChange("slug", e.target.value)}
            size="small"
            helperText="URL-friendly version of the title"
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Excerpt"
            value={formData.excerpt || ""}
            onChange={(e) => handleChange("excerpt", e.target.value)}
            multiline
            rows={2}
            size="small"
            placeholder="Brief summary of the post"
            error={!!errors.excerpt}
            helperText={
              errors.excerpt ||
              "A short description for previews (150-200 characters)"
            }
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Content"
            value={formData.content || ""}
            onChange={(e) => handleChange("content", e.target.value)}
            multiline
            rows={12}
            size="small"
            placeholder="Write your blog post content here..."
            error={!!errors.content}
            helperText={errors.content}
          />
        </Grid>

        {/* Images */}
        <Grid item xs={12}>
          <Typography
            variant="subtitle2"
            fontWeight="bold"
            color="text.secondary"
          >
            Images
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 2 }}>
            <Button
              variant="outlined"
              startIcon={<Upload />}
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              {uploading ? <CircularProgress size={24} /> : "Upload Image"}
            </Button>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleImageUpload}
              accept="image/*"
              style={{ display: "none" }}
            />
            <Typography variant="caption" color="text.secondary">
              JPG, PNG, GIF up to 5MB
            </Typography>
          </Box>

          {formData.images.length > 0 && (
            <ImageList cols={4} gap={8}>
              {formData.images.map((img, index) => (
                <ImageListItem key={index} sx={{ position: "relative" }}>
                  <img
                    src={img}
                    alt={`Upload ${index + 1}`}
                    style={{
                      width: "100%",
                      height: 100,
                      objectFit: "cover",
                      borderRadius: 4,
                    }}
                  />
                  <IconButton
                    size="small"
                    sx={{
                      position: "absolute",
                      top: 4,
                      right: 4,
                      bgcolor: "white",
                      "&:hover": { bgcolor: "#f5f5f5" },
                    }}
                    onClick={() => handleRemoveImage(img)}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                  {img === formData.featuredImage && (
                    <Chip
                      label="Featured"
                      size="small"
                      color="primary"
                      sx={{
                        position: "absolute",
                        bottom: 4,
                        left: 4,
                        height: 20,
                        fontSize: "10px",
                      }}
                    />
                  )}
                </ImageListItem>
              ))}
            </ImageList>
          )}
        </Grid>

        {/* Category & Tags */}
        <Grid item xs={12}>
          <Typography
            variant="subtitle2"
            fontWeight="bold"
            color="text.secondary"
          >
            Category & Tags
          </Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth size="small">
            <InputLabel>Category</InputLabel>
            <Select
              value={formData.category || ""}
              onChange={(e) => handleChange("category", e.target.value)}
              label="Category"
              error={!!errors.category}
            >
              {BLOG_CATEGORIES.map((cat) => (
                <MenuItem key={cat.value} value={cat.value}>
                  {cat.label}
                </MenuItem>
              ))}
            </Select>
            {errors.category && (
              <Typography variant="caption" color="error">
                {errors.category}
              </Typography>
            )}
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <Box sx={{ display: "flex", gap: 1 }}>
            <TextField
              fullWidth
              size="small"
              label="Tags"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && handleTagAdd()}
              placeholder="Add tags..."
            />
            <Button
              variant="outlined"
              onClick={handleTagAdd}
              size="small"
              startIcon={<Add />}
            >
              Add
            </Button>
          </Box>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1 }}>
            {formData.tags.map((tag) => (
              <Chip
                key={tag}
                label={tag}
                size="small"
                onDelete={() => handleTagRemove(tag)}
              />
            ))}
          </Box>
        </Grid>

        {/* SEO Settings */}
        <Grid item xs={12}>
          <Typography
            variant="subtitle2"
            fontWeight="bold"
            color="text.secondary"
          >
            SEO Settings
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="SEO Title"
            value={formData.seo?.title || ""}
            onChange={(e) => handleSeoChange("title", e.target.value)}
            size="small"
            helperText="Leave blank to use post title"
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="SEO Description"
            value={formData.seo?.description || ""}
            onChange={(e) => handleSeoChange("description", e.target.value)}
            multiline
            rows={2}
            size="small"
            helperText="Meta description for search engines (150-160 characters)"
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="SEO Keywords"
            value={formData.seo?.keywords?.join(", ") || ""}
            onChange={(e) =>
              handleSeoChange(
                "keywords",
                e.target.value.split(",").map((k) => k.trim()),
              )
            }
            size="small"
            placeholder="Enter keywords separated by commas"
          />
        </Grid>

        {/* Actions */}
        <Grid item xs={12}>
          <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
            <Button
              variant="outlined"
              startIcon={<Cancel />}
              onClick={onCancel}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={loading ? <CircularProgress size={20} /> : <Save />}
              disabled={loading}
              sx={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #5a67d8 0%, #6b46a1 100%)",
                },
              }}
            >
              {initialData ? "Update Post" : "Create Post"}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default BlogForm;
